/* eslint-disable no-param-reassign */
import produce from 'immer'
import {
  cloneDeep,
  filter,
  findIndex,
  forOwn,
  get,
  indexOf,
  keyBy,
  remove,
  set,
  size,
  sortBy,
} from 'lodash'
import { v4 as uuid } from 'uuid'

import { filterArray } from '../reducerUtils'
import { AnyObject, IField, IFilter, IUi } from '../types'

type TSort = {
  field: string[]
  direction: 'asc' | 'desc'
}
type TGroup = {
  operator: () => void
}

type TItem = {
  id: string
}

interface IIncludedFilter {
  useDeliveryAddress: boolean
  usePickupAddress: boolean
}
interface IInitFilter {
  page: number
  filter: IField[]
  sort: TSort[]
}

export interface DraftProps {
  itemsById: AnyObject
  itemsByIndex: AnyObject
  totalCount: number
  loadedPages: {
    [key: string]: any
  }
  filterMode: string
  initialFilter: Array<IField> // untouched to restore
  filter: IInitFilter
  filterForm: IInitFilter
  filterFormLocal: IInitFilter
  isLoading: boolean
  initialLoadDone: boolean
  ui: IUi
  sortableColumns: Array<string>
  useLocalStateFiltering: boolean
  // local state handling
  itemsToRestoreFrom: Array<TItem>
  currentRequestId: string
  customData: AnyObject
  customHeaders: AnyObject
  customPayload: AnyObject
  fulltextSearchLoading: boolean
  allIds: Array<number> | undefined
  includedFilters?: IIncludedFilter
  grouping?: TGroup
  currentItemsArr?: AnyObject[]
  groupedItems?: TGroup[]
  localFilterConfig?: AnyObject
}
type TGenerateInitialState = {
  initFilter?: IFilter
  initialState?: AnyObject
}

export function generateInitialState(props: TGenerateInitialState): DraftProps {
  const { initFilter = {}, initialState = {} } = props

  const generatedInitFilter: IInitFilter = {
    page: 1,
    filter: [],
    sort: [
      {
        field: ['id'],
        direction: 'desc',
      },
    ],
    ...initFilter,
  }

  return {
    itemsById: {},
    itemsByIndex: {},
    totalCount: 0,
    loadedPages: {},
    filterMode: 'fulltext',
    initialFilter: generatedInitFilter.filter, // untouched to restore
    filter: generatedInitFilter,
    filterForm: generatedInitFilter,
    filterFormLocal: generatedInitFilter,
    isLoading: false,
    initialLoadDone: false,
    ui: {
      currentRow: 0,
      isDetailOpen: false,
    },
    sortableColumns: ['id'],
    useLocalStateFiltering: false,
    // local state handling
    itemsToRestoreFrom: [],
    currentRequestId: uuid(),
    customData: {},
    customHeaders: {},
    customPayload: {},
    fulltextSearchLoading: false,
    allIds: undefined,
    ...initialState,
  }
}
type TGenerateActionHandlers = {
  constants: {
    [key: string]: string
  }
  actionHandlerCallbacks?: {
    [key: string]: (d: unknown) => void
  }
}
export function generateActionHandlers(
  props: TGenerateActionHandlers,
): AnyObject {
  const { constants } = props
  const handleSetLoading = produce(
    (draft: DraftProps, { payload: loadingStatus }) => {
      draft.isLoading = loadingStatus
    },
  )

  const handleSetRequestId = produce((draft: DraftProps, { payload: id }) => {
    draft.currentRequestId = id
  })

  const handleSetGrouping = produce(
    (draft: DraftProps, { payload: grouping }) => {
      draft.grouping = grouping
      const groupedItems = grouping.operator(draft.currentItemsArr)
      draft.groupedItems = groupedItems

      //draft.itemsById = keyBy(sortedResults, "id")
      //draft.itemsToRestoreFrom = sortedResults
    },
  )
  const handleSetItems = produce((draft: DraftProps, { payload: results }) => {
    if (size(results) === 0) {
      draft.itemsById = {}
      draft.itemsByIndex = {}
      draft.itemsToRestoreFrom = []
      draft.currentItemsArr = []
      draft.totalCount = 0
    } else {
      const sortDirection = draft.filterForm.sort[0].direction

      const ascSortResults = sortBy(results, [
        (item) => {
          return get(item, draft.filterForm.sort[0].field[0])
        },
      ])

      const sortedResults =
        sortDirection === 'asc' ? ascSortResults : ascSortResults.reverse()

      sortedResults.forEach((currentItem) => {
        const index = indexOf(sortedResults, currentItem)
        draft.itemsByIndex[index] = currentItem.id
      })

      draft.itemsById = keyBy(sortedResults, 'id')
      draft.itemsToRestoreFrom = sortedResults
      draft.currentItemsArr = sortedResults
      draft.totalCount = size(draft.itemsById)
    }
  })

  const handleSortLocal = produce((draft) => {
    const sortDirection = draft.filterForm.sort[0].direction

    const ascSortResults = sortBy(draft.currentItemsArr, [
      (item) => {
        return get(item, draft.filterForm.sort[0].field[0])
      },
    ])

    const sortedResults =
      sortDirection === 'asc' ? ascSortResults : ascSortResults.reverse()

    draft.itemsById = keyBy(sortedResults, 'id')
    //draft.itemsToRestoreFrom = sortedResults

    draft.currentItemsArr = sortedResults

    sortedResults.forEach((currentItem) => {
      const index = indexOf(sortedResults, currentItem)
      draft.itemsByIndex[index] = currentItem.id
    })
  })

  const handleApplyLocalValueFilter = produce(
    (draft: DraftProps, { payload: { valueFilter } }) => {
      // move to sequential processing

      const keyedFilters = keyBy(valueFilter, 'field')

      const valueFilteredItems = filter(draft.itemsToRestoreFrom, (item) => {
        for (const key in keyedFilters) {
          if (Object.prototype.hasOwnProperty.call(keyedFilters, key)) {
            if (
              get(item, `${key}`).toString().toLowerCase() !==
              get(keyedFilters, `${key}.value`).toString().toLowerCase()
            ) {
              return false
            }
          }
        }
        return true
      })

      draft.currentItemsArr = valueFilteredItems

      const tempItemsById = keyBy(valueFilteredItems, 'id')

      const newItemsByIndex = {}

      draft.itemsToRestoreFrom.forEach((currentItem) => {
        const index = indexOf(draft.itemsToRestoreFrom, currentItem)
        newItemsByIndex[index] = currentItem.id
      })

      const tempItemsByIndex = filter(newItemsByIndex, (item) => {
        if (get(tempItemsById, `${item}`)) {
          return true
        }
        return false
      })

      draft.itemsById = tempItemsById
      draft.itemsByIndex = tempItemsByIndex
    },
  )

  const handleApplyLocalFulltextFilter = produce(
    (draft: DraftProps, { payload: { fullTextFilter } }) => {
      const fulltextConfig = get(draft.localFilterConfig, 'fulltext', undefined)

      const ftsValue = get(fullTextFilter, 'value')

      draft.currentItemsArr = filterArray(
        draft.itemsToRestoreFrom,
        get(fulltextConfig, 'keys', []),
        ftsValue,
        fulltextConfig,
      )

      const tempItemsById = keyBy(
        filterArray(
          draft.itemsToRestoreFrom,
          get(fulltextConfig, 'keys', []),
          ftsValue,
          fulltextConfig,
        ),
        'id',
      )

      const newItemsByIndex = {}

      draft.itemsToRestoreFrom.forEach((currentItem) => {
        const index = indexOf(draft.itemsToRestoreFrom, currentItem)
        newItemsByIndex[index] = currentItem.id
      })

      const tempItemsByIndex = filter(newItemsByIndex, (item) => {
        if (get(tempItemsById, `${item}`)) {
          return true
        }
        return false
      })

      draft.itemsById = tempItemsById
      draft.itemsByIndex = tempItemsByIndex
    },
  )

  const handleRequest = produce(
    (draft: DraftProps, { payload: { startIndex, stopIndex } }) => {
      if (!draft.initialLoadDone) draft.isLoading = true
      // nullify loading entries
      for (let i = startIndex; i <= stopIndex; i++) {
        draft.itemsByIndex[i] = null
      }
    },
  )

  const handleReceive = produce(
    (
      draft,
      {
        payload: {
          itemsById,
          itemsByIndex,
          page,
          totalCount,
          customData,
          allIds,
        },
      },
    ) => {
      if (itemsById) {
        forOwn(itemsById, (value, key) => {
          draft.itemsById[key] = value
        })
        forOwn(itemsByIndex, (value, key) => {
          draft.itemsByIndex[key] = value
        })
        draft.loadedPages[page] = true
        draft.totalCount = totalCount
        draft.customData = customData
        draft.allIds = allIds
      }

      draft.isLoading = false
      draft.initialLoadDone = true
      draft.fulltextSearchLoading = false
    },
  )

  const handleSetFilter = produce((draft: DraftProps, { payload: values }) => {
    const index = findIndex(
      draft.filterForm.filter,
      (field) => field.field === values.name,
    )

    if (index != -1) {
      draft.filterForm.filter[index] = {
        field: values.name,
        value: values.value,
      }
    } else {
      draft.filterForm.filter.push({ field: values.name, value: values.value })
    }
  })

  const handleSetSort = produce((draft: DraftProps, { payload: field }) => {
    draft.filterForm.sort[0] = {
      field: [field.field],
      direction: draft.filterForm.sort[0].direction === 'asc' ? 'desc' : 'asc',
    }
  })

  const handleSetIncluded = produce((draft: DraftProps, { payload }) => {
    set(draft.includedFilters as any, `${payload.name}`, payload.value)
  })

  const handleApplyFilter = produce((draft: DraftProps, { payload }) => {
    draft.isLoading = false
    draft.fulltextSearchLoading = false
    draft.filter = cloneDeep(draft.filterForm)
    draft.itemsById = {}
    draft.itemsByIndex = {}
    draft.loadedPages = {}
    draft.totalCount = 0

    if (get(payload, 'reset.isFulltextSearch')) {
      draft.fulltextSearchLoading = true
    } else {
      draft.isLoading = true
    }
  })

  const handleSaveUi = produce(
    (draft: DraftProps, { payload: { currentRow, isDetailOpen } }) => {
      if (isDetailOpen !== undefined) {
        draft.ui.isDetailOpen = isDetailOpen
      }
      if (currentRow || currentRow === 0) {
        draft.ui.currentRow = currentRow
      }
    },
  )

  const handleSetFilterMode = produce((draft: DraftProps, { payload }) => {
    draft.filterForm.filter = []
    draft.filterMode = payload
  })

  const handleClearFilters = produce(
    (draft: DraftProps, { payload: { onlyFts } }) => {
      if (onlyFts) {
        remove(draft.filterForm.filter, (filter) => filter.name === 'fulltext')
      } else {
        draft.filterForm.filter = draft.initialFilter
        if (draft.includedFilters) {
          draft.includedFilters.useDeliveryAddress = true
          draft.includedFilters.usePickupAddress = true
        }
      }
    },
  )

  const handleSetCustomHeaders = produce((draft: DraftProps, { payload }) => {
    draft.customHeaders = payload
  })

  const handleSetCustomPayload = produce((draft: DraftProps, { payload }) => {
    draft.customPayload = payload
  })

  return {
    [constants.REQUEST]: handleRequest,
    [constants.RECEIVE]: handleReceive,
    [constants.SET_FILTER]: handleSetFilter,
    [constants.APPLY_FILTER]: handleApplyFilter,
    [constants.SAVE_UI]: handleSaveUi,
    [constants.SET_FILTER_MODE]: handleSetFilterMode,
    [constants.SET_INCLUDED]: handleSetIncluded,
    [constants.CLEAR_FILTERS]: handleClearFilters,
    [constants.SET_SORT]: handleSetSort,
    [constants.APPLY_LOCAL_FULLTEXTFILTER]: handleApplyLocalFulltextFilter,
    [constants.SET_ITEMS]: handleSetItems,
    [constants.SORT_LOCAL]: handleSortLocal,
    [constants.APPLY_LOCAL_VALUEFILTER]: handleApplyLocalValueFilter,
    [constants.SET_LOADING]: handleSetLoading,
    [constants.SET_GROUPING]: handleSetGrouping,
    [constants.SET_REQUEST_ID]: handleSetRequestId,
    [constants.SET_CUSTOM_HEADERS]: handleSetCustomHeaders,
    [constants.SET_CUSTOM_PAYLOAD]: handleSetCustomPayload,
  }
}
